import {
  ArrowDown, ArrowUp, ChevronDown, ChevronRight, Clock, Copy, Globe, Pencil, Plus, Search, Trash2, UserPlus,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Callout, EmptyState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Avatar, Switch } from "@/components/ui/misc";
import { MultiSelect, type MultiOption } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  addDays, dayLabel, localDay, slotTime, slotsByDay,
  CALENDAR_TYPES, DISTRIBUTION_INFO, DURATION_PRESETS, LOCALES, LOCATION_KINDS, LOCATION_LABELS,
  LOCATION_PLACEHOLDERS, PRIORITIES, PRIORITY_LABELS, RESCHEDULE_INFO, TYPE_INFO, WEEKDAYS, WEEKDAY_LABELS, WEEKDAY_SHORT,
  allDay, copyToAllDays, dayProblem, hoursSummary, isAllDay, moveItem, minutesText, newStaff, nextRange, officeHours,
  rangesOn, singleHost, sortRanges, staffName,
  timeChoices, timeLabel, timezoneOptions, weekFrom, type CalendarFormValues,
} from "@/lib/calendars";
import { UserSearchMore, useUserSearch } from "@/features/user-search";
import { withPicked } from "@/lib/paging";
import { useCalendarSlots } from "@/lib/queries";
import type {
  AvailabilityRange, Calendar, CalendarDistribution, CalendarStaff, RescheduleOwner, Weekday,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The four sections of a calendar's page (ACL-288): its details, its staff and where they meet,
 * each one's weekly hours, and the booking rules. Each is a view of the one form the page holds and
 * saves; none of them saves on its own.
 */

export interface SectionProps {
  values: CalendarFormValues;
  update: (change: (v: CalendarFormValues) => CalendarFormValues) => void;
  problems: Record<string, string>;
  readOnly: boolean;
}

// --- details -------------------------------------------------------------------------------------

export function DetailsSection({ values, update, problems, readOnly }: SectionProps) {
  return (
    <Card>
      <CardHeader title="Basic details" description="What the calendar is called and how it books." />
      <CardBody className="space-y-4">
        <Field label="Name" htmlFor="cal-name" required error={problems.name}>
          <Input
            value={values.name}
            maxLength={120}
            disabled={readOnly}
            invalid={Boolean(problems.name)}
            onChange={(e) => update((v) => ({ ...v, name: e.target.value }))}
          />
        </Field>
        <Field label="Description" htmlFor="cal-description" hint="For your team; contacts do not see it.">
          <Textarea
            rows={3}
            maxLength={1000}
            disabled={readOnly}
            value={values.description ?? ""}
            onChange={(e) => update((v) => ({ ...v, description: e.target.value }))}
          />
        </Field>
        <Field
          label="Calendar type"
          htmlFor="cal-type"
          hint={`${TYPE_INFO[values.calendar_type].description} Fixed once an appointment is booked on it.`}
        >
          <Select
            value={values.calendar_type}
            disabled={readOnly}
            onValueChange={(t) => update((v) => ({ ...v, calendar_type: t as CalendarFormValues["calendar_type"] }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CALENDAR_TYPES.map((t) => (
                <SelectItem key={t} value={t} description={TYPE_INFO[t].description}>
                  {TYPE_INFO[t].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <label className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
          <span>
            <span className="block text-sm font-medium text-text">Active</span>
            <span className="block text-xs text-text-muted">
              An inactive calendar takes no new bookings. Its appointments stay.
            </span>
          </span>
          <Switch
            checked={values.active}
            disabled={readOnly}
            onCheckedChange={(active) => update((v) => ({ ...v, active }))}
            aria-label="Active"
          />
        </label>
      </CardBody>
    </Card>
  );
}

// --- staff & location ----------------------------------------------------------------------------

export function StaffSection({ values, update, problems, readOnly }: SectionProps) {
  const roundRobin = values.calendar_type === "round_robin";
  const single = singleHost(values.calendar_type);
  // Searched on the server (ACL-491): this once filtered the first 50 users by email, and nobody
  // after them could be put on a calendar.
  const [query, setQuery] = React.useState("");
  const people = useUserSearch(query, { is_active: true }, { enabled: !readOnly });
  const byId = new Map(people.users.map((u) => [u.id, u]));

  const setStaff = (ids: number[]) =>
    update((v) => {
      const kept = new Map(v.staff.map((s) => [s.user_id, s]));
      return {
        ...v,
        staff: ids.flatMap((id) => {
          const existing = kept.get(id);
          if (existing) return [existing];
          const user = byId.get(id);
          return user ? [newStaff(user)] : [];
        }),
      };
    });

  // The list's order is the calendar's staff order: round robin's last tie-break and a
  // collaborative meeting's owner (ADR-085).
  const move = (index: number, by: -1 | 1) =>
    update((v) => ({ ...v, staff: moveItem(v.staff, index, by) }));

  const change = (userId: number, patch: Partial<CalendarStaff>) =>
    update((v) => ({ ...v, staff: v.staff.map((s) => (s.user_id === userId ? { ...s, ...patch } : s)) }));

  // Who is on the calendar is listed from the calendar itself, deactivated or not, so it is there
  // to unpick whatever page of everyone has loaded; the matches come from the server.
  const onCalendar: MultiOption[] = values.staff.map((s) => ({
    value: String(s.user_id), label: staffName(s), hint: s.user_active ? s.email : "Deactivated",
  }));
  const matches: MultiOption[] = people.users.map((u) => ({ value: String(u.id), label: staffName(u), hint: u.email }));
  const options = withPicked(onCalendar, matches, people.searching, (o) => o.value);
  const host: ComboboxOption | null = onCalendar[0] ?? null;
  const hostOptions = withPicked(host ? [host] : [], matches, people.searching, (o) => o.value);
  // Read-only, nobody is searched for, and "0 loaded" would be news about nothing.
  const more = readOnly ? undefined : <UserSearchMore search={people} />;

  return (
    <Card>
      <CardHeader title="Staff & meeting location" description="Who takes meetings on this calendar, and where they meet." />
      <CardBody className="space-y-5">
        {roundRobin && (
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-text">Meeting distribution</legend>
            <ChoiceCards
              value={values.distribution ?? "availability"}
              disabled={readOnly}
              choices={DISTRIBUTION_INFO}
              onChange={(d) => update((v) => ({ ...v, distribution: d as CalendarDistribution }))}
            />
          </fieldset>
        )}
        {roundRobin && (
          <label className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
            <span>
              <span className="block text-sm font-medium text-text">Prefer the contact's owner</span>
              <span className="block text-xs text-text-muted">
                Book a returning contact with whoever hosted their last meeting, when that person is on this calendar and free.
              </span>
            </span>
            <Switch
              checked={values.prefer_contact_owner}
              disabled={readOnly}
              onCheckedChange={(prefer) => update((v) => ({ ...v, prefer_contact_owner: prefer }))}
              aria-label="Prefer the contact's owner"
            />
          </label>
        )}

        <Field
          label={single ? "Host" : "Staff"}
          htmlFor="cal-staff"
          error={problems.staff}
          hint={single ? "One team member takes every meeting on this calendar." : "Anyone active in your organization."}
        >
          {single ? (
            <Combobox
              label="Host"
              options={hostOptions}
              value={host?.value ?? null}
              picked={host}
              onChange={(id) => id !== null && setStaff([Number(id)])}
              onSearch={setQuery}
              searchedFor={people.searchedFor}
              loading={people.loading}
              footer={more}
              placeholder="Pick the host"
              clearLabel={null}
              emptyText="No users to pick"
              disabled={readOnly}
            />
          ) : (
            <MultiSelect
              label="Staff"
              options={options}
              value={values.staff.map((s) => String(s.user_id))}
              onChange={(ids) => !readOnly && setStaff(ids.map(Number))}
              onSearch={setQuery}
              searchedFor={people.searchedFor}
              picked={onCalendar}
              loading={people.loading}
              footer={more}
              placeholder="Nobody yet"
              emptyText="No users to add"
            />
          )}
        </Field>

        {values.staff.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title="Nobody on this calendar yet"
            description="Nothing can be booked on it until someone is."
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {values.staff.map((s, index) => (
              <li key={s.user_id} className="grid gap-3 p-3 md:grid-cols-[minmax(0,1fr)_10rem_minmax(0,1.4fr)_auto] md:items-start">
                <span className="flex min-w-0 items-center gap-2 pt-1">
                  <Avatar name={s.display_name} email={s.email} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-text">{staffName(s)}</span>
                    <span className="block truncate text-xs text-text-muted">
                      {s.user_active ? s.email : "Deactivated: never booked"}
                    </span>
                  </span>
                </span>
                <Select
                  value={s.priority}
                  disabled={readOnly}
                  onValueChange={(p) => change(s.user_id, { priority: p as CalendarStaff["priority"] })}
                >
                  <SelectTrigger aria-label={`${staffName(s)} priority`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="space-y-2">
                  <Select
                    value={s.location_kind}
                    disabled={readOnly}
                    onValueChange={(k) => change(s.user_id, { location_kind: k as CalendarStaff["location_kind"] })}
                  >
                    <SelectTrigger aria-label={`${staffName(s)} meeting location`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATION_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {LOCATION_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    aria-label={`${staffName(s)} meeting details`}
                    value={s.location_detail ?? ""}
                    disabled={readOnly}
                    maxLength={1000}
                    invalid={Boolean(problems[`location.${s.user_id}`])}
                    placeholder={LOCATION_PLACEHOLDERS[s.location_kind]}
                    onChange={(e) => change(s.user_id, { location_detail: e.target.value })}
                  />
                  {problems[`location.${s.user_id}`] && (
                    <p className="text-xs text-danger-11">{problems[`location.${s.user_id}`]}</p>
                  )}
                </div>
                {!readOnly && (
                  <span className="flex items-center">
                    {values.staff.length > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${staffName(s)} up`}
                          title="Move up"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${staffName(s)} down`}
                          title="Move down"
                          disabled={index === values.staff.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          <ArrowDown />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${staffName(s)}`}
                      onClick={() => setStaff(values.staff.filter((x) => x.user_id !== s.user_id).map((x) => x.user_id))}
                    >
                      <Trash2 />
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {roundRobin && (
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-text">When a meeting is rescheduled</legend>
            <ChoiceCards
              value={values.reschedule_owner ?? "keep"}
              disabled={readOnly}
              choices={RESCHEDULE_INFO}
              onChange={(r) => update((v) => ({ ...v, reschedule_owner: r as RescheduleOwner }))}
            />
          </fieldset>
        )}
      </CardBody>
    </Card>
  );
}

function ChoiceCards({
  value, choices, onChange, disabled,
}: {
  value: string;
  choices: Record<string, { label: string; description: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
      {Object.entries(choices).map(([key, info]) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          disabled={disabled}
          onClick={() => onChange(key)}
          className={cn(
            "rounded-lg border p-3 text-left transition-colors disabled:cursor-default",
            value === key ? "border-accent-8 bg-accent-2" : "border-border hover:border-border-strong",
          )}
        >
          <span className="block text-sm font-medium text-text">{info.label}</span>
          <span className="block text-xs text-text-muted">{info.description}</span>
        </button>
      ))}
    </div>
  );
}

// --- availability --------------------------------------------------------------------------------

/**
 * Each staff member's weekly hours (GoHighLevel's "Availability schedule"): a card per staff member
 * with their week in one line, opening a drawer to change it. Hours are 24 hours every day until
 * "Custom hours" is switched on.
 *
 * Bulk edit, as GoHighLevel's: pick the staff in a menu, review their hours in a drawer, then
 * "Bulk edit" opens one week that replaces all of theirs. Every change goes into the page's form;
 * the page's own Save keeps it.
 */
export function AvailabilitySection({ values, update, problems, readOnly }: SectionProps) {
  const [open, setOpen] = React.useState<number | null>(null);
  const [bulk, setBulk] = React.useState<number[] | null>(null);
  const [bulkEditing, setBulkEditing] = React.useState(false);

  if (values.staff.length === 0) {
    return (
      <Card>
        <CardHeader title="Availability schedule" />
        <CardBody>
          <EmptyState title="Add staff first" description="Hours belong to a staff member on this calendar." />
        </CardBody>
      </Card>
    );
  }

  const setHours = (ids: number[], availability: AvailabilityRange[]) =>
    update((v) => ({
      ...v,
      staff: v.staff.map((s) => (ids.includes(s.user_id) ? { ...s, availability: sortRanges(availability) } : s)),
    }));
  const editing = values.staff.find((s) => s.user_id === open) ?? null;
  const chosen = values.staff.filter((s) => bulk?.includes(s.user_id));
  const clock = { timezone: values.timezone, format: values.time_format, weekStartsOn: values.week_starts_on };

  return (
    <Card>
      <CardHeader title="Availability schedule" description="Set when meetings can be booked based on staff availability." />
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-text" title={`Hours are on the calendar's clock: ${values.timezone.replace(/_/g, " ")}.`}>
            Booking availability
          </p>
          {!readOnly && values.staff.length > 1 && <BulkPicker staff={values.staff} onPick={setBulk} />}
        </div>
        <ul className="grid gap-2 md:grid-cols-2">
          {values.staff.map((s) => {
            const wrong = Object.keys(problems).some((k) => k.startsWith(`hours.${s.user_id}.`));
            return (
              <li key={s.user_id}>
                <button
                  type="button"
                  onClick={() => setOpen(s.user_id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:border-accent-8 hover:bg-surface-hover",
                    wrong ? "border-danger-8" : "border-border",
                  )}
                >
                  <Avatar name={s.display_name} email={s.email} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text">{staffName(s)}</span>
                    <span className={cn("block truncate text-xs", wrong ? "text-danger-11" : "text-text-muted")}>
                      {wrong ? "These hours need fixing" : hoursSummary(s.availability, values.time_format, values.week_starts_on)}
                    </span>
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-text-muted" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      </CardBody>

      <HoursDrawer
        who={editing}
        clock={clock}
        readOnly={readOnly}
        onClose={() => setOpen(null)}
        onSave={(availability) => {
          if (editing) setHours([editing.user_id], availability);
          setOpen(null);
        }}
      />
      <BulkDrawer
        staff={chosen}
        open={bulk !== null}
        calendarName={values.name}
        clock={clock}
        onEdit={() => setBulkEditing(true)}
        onClose={() => setBulk(null)}
      />
      <BulkHoursDialog
        open={bulkEditing}
        staff={chosen}
        clock={clock}
        onClose={() => setBulkEditing(false)}
        onSave={(availability) => {
          setHours(chosen.map((s) => s.user_id), availability);
          setBulkEditing(false);
        }}
      />
    </Card>
  );
}

export interface Clock {
  timezone: string;
  format: "12h" | "24h";
  weekStartsOn: Weekday;
}

/** Bulk edit's first step: search the staff, tick who, then "Edit hours". */
function BulkPicker({ staff, onPick }: { staff: CalendarStaff[]; onPick: (ids: number[]) => void }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [ticked, setTicked] = React.useState<number[]>([]);
  const needle = query.trim().toLowerCase();
  const shown = staff.filter((s) => !needle || staffName(s).toLowerCase().includes(needle) || s.email.toLowerCase().includes(needle));
  const all = ticked.length === staff.length;
  const toggle = (id: number) => setTicked((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setQuery("");
          setTicked([]);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="subtle" size="sm">
          <Pencil />
          Bulk edit
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="border-b border-border p-2">
          <Input leading={<Search className="size-3.5" />} placeholder="Search users" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="max-h-64 overflow-y-auto p-1.5 scrollbar-subtle">
          {!needle && (
            <label className="flex items-center gap-2.5 rounded-md border-b border-border px-2 py-2 text-sm text-text hover:bg-surface-hover">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent-9)]"
                checked={all}
                onChange={() => setTicked(all ? [] : staff.map((s) => s.user_id))}
              />
              All
            </label>
          )}
          {shown.map((s) => (
            <label key={s.user_id} className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm text-text hover:bg-surface-hover">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent-9)]"
                checked={ticked.includes(s.user_id)}
                onChange={() => toggle(s.user_id)}
              />
              <span className="truncate">{staffName(s)}</span>
            </label>
          ))}
          {shown.length === 0 && <p className="px-2 py-2 text-sm text-text-muted">Nobody matches.</p>}
        </div>
        <div className="flex justify-center border-t border-border p-2">
          <Button
            variant="subtle"
            size="sm"
            disabled={ticked.length === 0}
            onClick={() => {
              onPick(staff.filter((s) => ticked.includes(s.user_id)).map((s) => s.user_id));
              setOpen(false);
            }}
          >
            <Clock className="size-3.5" />
            Edit hours
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Bulk edit's second step: the chosen staff's hours as they are, and the way to replace them. */
function BulkDrawer({
  staff, open, calendarName, clock, onEdit, onClose,
}: {
  staff: CalendarStaff[];
  open: boolean;
  calendarName: string;
  clock: Clock;
  onEdit: () => void;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (open) setExpanded(staff[0]?.user_id ?? null);
    // Opens with the first one expanded; only on opening.
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>
            Update availability for{" "}
            <span className="font-semibold">
              {staff.length} selected user{staff.length === 1 ? "" : "s"}
            </span>{" "}
            <span className="font-normal text-text-muted">– {calendarName}</span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Callout tone="warning">Bulk edit enabled. Changes will overwrite the existing schedules for all selected users.</Callout>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-text">Bulk edit weekly hours</p>
            <p className="text-sm text-text-muted">This will replace the weekly hours for all users listed below.</p>
            <button type="button" className="inline-flex items-center gap-1.5 pt-1 text-sm font-medium text-accent-11 hover:underline" onClick={onEdit}>
              <Pencil className="size-3.5" aria-hidden />
              Bulk edit
            </button>
          </div>
          <ul className="space-y-2">
            {staff.map((s) => {
              const isOpen = expanded === s.user_id;
              return (
                <li key={s.user_id} className="rounded-lg border border-border">
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : s.user_id)}
                  >
                    <Avatar name={s.display_name} email={s.email} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-text">{staffName(s)}</span>
                      <span className="block truncate text-xs text-text-muted">{hoursSummary(s.availability, clock.format, clock.weekStartsOn)}</span>
                    </span>
                    {isOpen ? <ChevronDown className="size-4 text-text-muted" aria-hidden /> : <ChevronRight className="size-4 text-text-muted" aria-hidden />}
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3">
                      <WeekView availability={s.availability} clock={clock} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Bulk edit's last step: one week for every chosen staff member. */
function BulkHoursDialog({
  open, staff, clock, onClose, onSave,
}: {
  open: boolean;
  staff: CalendarStaff[];
  clock: Clock;
  onClose: () => void;
  onSave: (availability: AvailabilityRange[]) => void;
}) {
  const [draft, setDraft] = React.useState<AvailabilityRange[]>(allDay());
  React.useEffect(() => {
    if (open) setDraft(staff[0]?.availability ?? allDay());
    // A fresh week each time it opens, starting from the first chosen staff member's.
  }, [open]);
  const broken = weekBroken(draft);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            Update weekly hours for {staff.length} user{staff.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2 rounded-lg bg-gray-2 p-3">
            <p className="text-sm font-medium text-text">Selected users:</p>
            <div className="flex flex-wrap gap-1.5">
              {staff.map((s) => (
                <span key={s.user_id} className="inline-flex max-w-48 items-center gap-1.5 rounded-full border border-border bg-surface py-0.5 pl-0.5 pr-2.5 text-xs text-text">
                  <Avatar name={s.display_name} email={s.email} className="size-5 text-[9px]" />
                  <span className="truncate">{staffName(s)}</span>
                </span>
              ))}
            </div>
          </div>
          <WeekEditor draft={draft} setDraft={setDraft} clock={clock} />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={broken} onClick={() => onSave(draft)}>
            Save weekly hours
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One staff member's week, in a drawer. */
function HoursDrawer({
  who, clock, readOnly, onClose, onSave,
}: {
  who: CalendarStaff | null;
  clock: Clock;
  readOnly: boolean;
  onClose: () => void;
  onSave: (availability: AvailabilityRange[]) => void;
}) {
  const [draft, setDraft] = React.useState<AvailabilityRange[]>(allDay());
  React.useEffect(() => {
    if (who) setDraft(who.availability);
    // A fresh draft each time the drawer opens on someone.
  }, [who?.user_id]);

  return (
    <Dialog open={who !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            {who && <Avatar name={who.display_name} email={who.email} />}
            {who ? staffName(who) : ""}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {readOnly ? <WeekView availability={draft} clock={clock} /> : <WeekEditor draft={draft} setDraft={setDraft} clock={clock} />}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button disabled={weekBroken(draft)} onClick={() => onSave(draft)}>
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function weekBroken(availability: AvailabilityRange[]): boolean {
  return WEEKDAYS.some((d) => dayProblem(rangesOn(availability, d)) !== null);
}

/**
 * What a week of hours is called where it is edited. A calendar's are when meetings can be booked;
 * a line's (ACL-567) are when it should be staffed.
 */
export interface WeekWords {
  /** The switch that turns the hours on, and what each side of it means. */
  custom: string;
  customOn: string;
  customOff: string;
  /** A day with no hours. */
  closed: string;
  /** Whose clock the hours are on. */
  zone: string;
}

const CALENDAR_WEEK: WeekWords = {
  custom: "Custom hours",
  customOn: "Bookable only in the hours below.",
  customOff: "Off: bookable 24 hours a day, every day.",
  closed: "Unavailable",
  zone: "the calendar's timezone (Booking rules)",
};

function ZoneLine({ timezone, words = CALENDAR_WEEK }: { timezone: string; words?: WeekWords }) {
  return (
    <p className="flex items-center gap-1.5 text-xs text-text-muted">
      <Globe className="size-3.5" aria-hidden />
      {timezone.replace(/_/g, " ")} · {words.zone}
    </p>
  );
}

/** A week as it reads: each day's ranges, "24 hours", or unavailable. */
function WeekView({
  availability, clock, words = CALENDAR_WEEK,
}: {
  availability: AvailabilityRange[];
  clock: Clock;
  words?: WeekWords;
}) {
  const allDayLong = isAllDay(availability);
  return (
    <div className="space-y-1 rounded-lg bg-gray-2 px-3 py-2">
      <ZoneLine timezone={clock.timezone} words={words} />
      <ul className="divide-y divide-border">
        {weekFrom(clock.weekStartsOn).map((d) => {
          const ranges = rangesOn(availability, d);
          return (
            <li key={d} className="flex items-start gap-6 py-2 text-sm">
              <span className="w-10 shrink-0 text-text">{WEEKDAY_SHORT[d]}</span>
              <span className={ranges.length ? "text-text" : "text-text-subtle"}>
                {allDayLong
                  ? "24 hours"
                  : ranges.length === 0
                    ? words.closed
                    : ranges.map((r) => `${timeLabel(r.start, clock.format)} – ${timeLabel(r.end, clock.format)}`).join(", ")}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A week to change: 24 hours a day until "Custom hours" is on, then a row of ranges per day.
 * `fixed` leaves the switch out and always shows the rows, for a form that asks the question
 * itself (a line's staffed hours, ACL-567).
 */
export function WeekEditor({
  draft, setDraft, clock, words = CALENDAR_WEEK, fixed = false,
}: {
  draft: AvailabilityRange[];
  setDraft: React.Dispatch<React.SetStateAction<AvailabilityRange[]>>;
  clock: Clock;
  words?: WeekWords;
  fixed?: boolean;
}) {
  const choices = React.useMemo(() => timeChoices(), []);
  const custom = fixed || !isAllDay(draft);
  const replaceDay = (day: Weekday, ranges: AvailabilityRange[]) =>
    setDraft((a) => sortRanges([...a.filter((r) => r.day !== day), ...ranges]));

  return (
    <div className="space-y-4">
      {!fixed && (
        <label className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
          <span>
            <span className="block text-sm font-medium text-text">{words.custom}</span>
            <span className="block text-xs text-text-muted">{custom ? words.customOn : words.customOff}</span>
          </span>
          <Switch checked={custom} aria-label={words.custom} onCheckedChange={(on) => setDraft(on ? officeHours() : allDay())} />
        </label>
      )}
      {!custom ? (
        <WeekView availability={draft} clock={clock} words={words} />
      ) : (
        <div className="space-y-2">
          {/* A form that asks the question itself shows its own timezone above. */}
          {!fixed && <ZoneLine timezone={clock.timezone} words={words} />}
          <ul className="divide-y divide-border rounded-lg border border-border px-3">
            {weekFrom(clock.weekStartsOn).map((day) => {
              const ranges = rangesOn(draft, day);
              const on = ranges.length > 0;
              const next = nextRange(draft, day);
              const problem = dayProblem(ranges);
              return (
                <li key={day} className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-2 py-3">
                  <label className="flex items-center gap-2 self-start pt-2">
                    <input
                      type="checkbox"
                      className="size-4 accent-[var(--color-accent-9)]"
                      checked={on}
                      onChange={(e) => replaceDay(day, e.target.checked ? [nextRange([], day)!] : [])}
                    />
                    <span className={cn("text-sm", on ? "font-medium text-text" : "text-text-muted")}>{WEEKDAY_SHORT[day]}</span>
                  </label>
                  <div className="space-y-2">
                    {!on && <p className="pt-2 text-sm text-text-subtle">{words.closed}</p>}
                    {ranges.map((r, i) => (
                      <div key={`${r.start}-${i}`} className="flex flex-wrap items-center gap-1.5">
                        <TimeSelect
                          label={`${WEEKDAY_LABELS[day]} range ${i + 1} start`}
                          value={r.start}
                          choices={choices.slice(0, -1)}
                          format={clock.format}
                          onChange={(start) => replaceDay(day, ranges.map((x, j) => (j === i ? { ...x, start } : x)))}
                        />
                        <span className="text-text-muted">–</span>
                        <TimeSelect
                          label={`${WEEKDAY_LABELS[day]} range ${i + 1} end`}
                          value={r.end}
                          choices={choices.slice(1)}
                          format={clock.format}
                          onChange={(end) => replaceDay(day, ranges.map((x, j) => (j === i ? { ...x, end } : x)))}
                        />
                        <span className="flex items-center">
                          {i === 0 && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Add a range on ${WEEKDAY_LABELS[day]}`}
                              disabled={!next}
                              onClick={() => next && replaceDay(day, [...ranges, next])}
                            >
                              <Plus />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove ${WEEKDAY_LABELS[day]} range ${i + 1}`}
                            onClick={() => replaceDay(day, ranges.filter((_, j) => j !== i))}
                          >
                            <Trash2 />
                          </Button>
                          {i === 0 && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Copy ${WEEKDAY_LABELS[day]} to every day`}
                              title="Copy to every day"
                              onClick={() => setDraft((a) => copyToAllDays(a, day))}
                            >
                              <Copy />
                            </Button>
                          )}
                        </span>
                      </div>
                    ))}
                    {problem && (
                      <p className="text-xs text-danger-11" role="alert">
                        {problem}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function TimeSelect({
  label, value, choices, format, onChange, disabled,
}: {
  label: string;
  value: string;
  choices: string[];
  format: "12h" | "24h";
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  // A saved time off the quarter hours (set through the API) stays pickable rather than blank.
  const all = choices.includes(value) ? choices : [...choices, value].sort();
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-40" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {all.map((t) => (
          <SelectItem key={t} value={t}>
            {timeLabel(t, format)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// --- booking rules -------------------------------------------------------------------------------

type NumberKey =
  | "duration_minutes" | "slot_interval_minutes" | "min_notice_minutes" | "pre_buffer_minutes"
  | "post_buffer_minutes" | "booking_window_days";

export function RulesSection({ values, update, problems, readOnly }: SectionProps) {
  const zones = React.useMemo(() => {
    const own = (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return undefined;
      }
    })();
    const list = timezoneOptions(own);
    return list.includes(values.timezone) ? list : [...list, values.timezone];
  }, [values.timezone]);
  const customDuration = !DURATION_PRESETS.includes(values.duration_minutes);

  const number = (key: NumberKey, label: string, hint: string, unit: string) => (
    <Field label={label} htmlFor={`cal-${key}`} hint={hint} error={problems[key]}>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        disabled={readOnly}
        invalid={Boolean(problems[key])}
        value={Number.isNaN(values[key]) ? "" : values[key]}
        trailing={<span className="text-xs text-text-muted">{unit}</span>}
        onChange={(e) => update((v) => ({ ...v, [key]: e.target.value === "" ? Number.NaN : Math.trunc(Number(e.target.value)) }))}
      />
    </Field>
  );

  const pick = <K extends "timezone" | "time_format" | "locale" | "week_starts_on">(
    key: K,
    label: string,
    options: { value: string; label: string }[],
    hint?: string,
  ) => (
    <Field label={label} htmlFor={`cal-${key}`} hint={hint}>
      <Select
        value={values[key]}
        disabled={readOnly}
        onValueChange={(next) => update((v) => ({ ...v, [key]: next }))}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Meeting" description="How long a meeting is, and how often one can start." />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Duration"
            htmlFor="cal-duration"
            error={problems.duration_minutes}
            hint={customDuration ? undefined : "How long each appointment is."}
          >
            <Select
              value={customDuration ? "custom" : String(values.duration_minutes)}
              disabled={readOnly}
              onValueChange={(d) =>
                update((v) => ({ ...v, duration_minutes: d === "custom" ? (customDuration ? v.duration_minutes : 120) : Number(d) }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_PRESETS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {minutesText(d)}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {customDuration ? (
            number("duration_minutes", "Custom duration", "5 minutes to a day.", "min")
          ) : (
            <div className="hidden sm:block" />
          )}
          {number("slot_interval_minutes", "Slot interval", "How often a slot starts: every 30 minutes gives 9:00, 9:30, …", "min")}
          {values.calendar_type === "class" && (
            <Field
              label="Seats per session"
              htmlFor="cal-seats"
              hint="How many contacts one class session holds. A full session is no longer offered."
              error={problems.seats_per_session}
            >
              <Input
                id="cal-seats"
                type="number"
                inputMode="numeric"
                min={1}
                max={500}
                disabled={readOnly}
                invalid={Boolean(problems.seats_per_session)}
                value={values.seats_per_session == null || Number.isNaN(values.seats_per_session) ? "" : values.seats_per_session}
                trailing={<span className="text-xs text-text-muted">seats</span>}
                onChange={(e) =>
                  update((v) => ({ ...v, seats_per_session: e.target.value === "" ? Number.NaN : Math.trunc(Number(e.target.value)) }))
                }
              />
            </Field>
          )}
          {number("booking_window_days", "Bookable range", "How far ahead a meeting can be booked.", "days")}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Notice & buffers" description="Room around each meeting that nothing else can be booked into." />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          {number("min_notice_minutes", "Minimum notice", `Nothing sooner than this from now. ${values.min_notice_minutes > 0 && !Number.isNaN(values.min_notice_minutes) ? `(${minutesText(values.min_notice_minutes)})` : ""}`, "min")}
          {number("pre_buffer_minutes", "Before each meeting", "Kept free before it starts.", "min")}
          {number("post_buffer_minutes", "After each meeting", "Kept free after it ends.", "min")}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Clock & region" description="The timezone the hours are written on, and how times are shown." />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          {pick("timezone", "Timezone", zones.map((z) => ({ value: z, label: z.replace(/_/g, " ") })), "Changing it moves every staff member's hours with it.")}
          {pick("time_format", "Time format", [
            { value: "12h", label: "12-hour (1:30 PM)" },
            { value: "24h", label: "24-hour (13:30)" },
          ])}
          {pick("locale", "Language / region", [...LOCALES])}
          {pick("week_starts_on", "Week starts on", (["sun", "mon", "sat"] as const).map((d) => ({ value: d, label: WEEKDAY_LABELS[d] })))}
        </CardBody>
      </Card>
    </div>
  );
}

// --- preview (ACL-289) ---------------------------------------------------------------------------

/**
 * The times the calendar offers over the next week, as the slot engine works them out from the
 * saved settings: the hours, the rules and the appointments already booked. The check an admin
 * makes after changing any of them.
 */
export function PreviewSection({ calendar, unsaved }: { calendar: Calendar; unsaved: boolean }) {
  const first = localDay(new Date().toISOString(), calendar.timezone);
  const last = addDays(first, 6);
  const { data, isPending, error } = useCalendarSlots(calendar.id, first, last);
  const names = new Map(calendar.staff.map((s) => [s.user_id, staffName(s)]));
  const days = data ? slotsByDay(data.slots, calendar.timezone, first, last) : [];
  const total = data?.slots.length ?? 0;

  return (
    <Card>
      <CardHeader
        title="Preview"
        description={`The times this calendar offers over the next 7 days, on ${calendar.timezone.replace(/_/g, " ")} time.`}
      />
      <CardBody className="space-y-4">
        {unsaved && <Callout tone="info">This shows the saved settings. Save to see your changes here.</Callout>}
        {!calendar.active && <Callout tone="warning">The calendar is inactive, so it offers nothing.</Callout>}
        {isPending ? (
          <div className="grid place-items-center py-10">
            <Spinner />
          </div>
        ) : error ? (
          <Callout tone="danger">Could not work out the slots. Please try again.</Callout>
        ) : total === 0 && calendar.active ? (
          <EmptyState
            title="Nothing bookable this week"
            description="Check that the calendar has staff, that they have hours, and that the minimum notice and bookable range leave room."
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {days.map(({ day, slots }) => (
              <li key={day} className="grid gap-2 p-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
                <span className="pt-0.5 text-sm font-medium text-text">{dayLabel(day, calendar.locale)}</span>
                {slots.length === 0 ? (
                  <span className="text-sm text-text-subtle">No slots</span>
                ) : (
                  <span className="flex flex-wrap gap-1.5">
                    {slots.map((s) => (
                      <span
                        key={s.start}
                        title={s.user_ids.map((id) => names.get(id) ?? `User ${id}`).join(", ")}
                        className="rounded-md border border-border bg-surface px-2 py-0.5 text-xs text-text"
                      >
                        {slotTime(s.start, calendar.timezone, calendar.time_format, calendar.locale)}
                      </span>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
